import React, { useContext, useEffect, useState } from 'react';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { auth } from '../../api/firebase/config';
import {
  gray100,
  primaryButtonBackgroundColor,
  gray40,
  gray20,
  white,
} from '../../constants/colors';
import { headerHeight, spHeaderHeight } from '../../constants/location';
import { AuthContext } from '../../contexts/auth';
import { PasswordChangeSchema } from '../../validation/user';
import type { PasswordChangeType } from '../../validation/user/userType';

import CustomTextfield from '../uiParts/CustomTextfield';
import Header from '../uiParts/Header';
import PasswordTextField from '../uiParts/PasswordTextfield';
import CustomSnackbar from '../uiParts/CustomSnackbar';
import CustomLoading from '../uiParts/CustomLoading';
import { AxiosError } from 'axios';
import { getErrorMessageFromAuthErrorCode } from '../../api/firebase/auth/error';
import { common } from '../../modules/error';
import MetaTitle from '../utils/MetaTitle';

const styles = {
  button: {
    width: '400px',
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
    '@media screen and (max-width: 600px)': {
      width: '100%',
    },
  },
  pageContainer: {
    pt: headerHeight,
    backgroundColor: gray20,
    minHeight: `calc(100vh - ${headerHeight})`,
    '@media screen and (max-width: 600px)': {
      pt: spHeaderHeight,
      minHeight: `calc(100vh - ${spHeaderHeight})`,
    },
  },
  PasswordChangeContainer: {
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    flexDirection: 'column',
    p: '39px',
    '@media screen and (max-width: 600px)': {
      width: '100%',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
      margin: '0 16px',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: `calc(100vh - ${headerHeight})`,
    '@media screen and (max-width:600px)': {
      width: '100%',
    },
    width: '560px',
    margin: '0 auto',
  },
  link: {
    color: gray100,
    display: 'block',
    fontWeight: '500',
    m: '24px 0px 0px 0px',
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
};

const PasswordChange: React.FC = (): JSX.Element => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordChangeType>({
    resolver: yupResolver(PasswordChangeSchema),
    defaultValues: {
      email: email,
    },
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('password') === undefined ||
        '' ||
        watch('passwordConfirm') === undefined ||
        '' ||
        Boolean(errors.password) ||
        Boolean(errors.passwordConfirm) ||
        watch('password') !== watch('passwordConfirm')
      );
    }
    return false;
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const oobCode = queryParams.get('oobCode');

    if (oobCode) {
      setCode(oobCode);
      setLoading(true);
      verifyPasswordResetCode(auth, oobCode)
        .then(email => {
          setEmail(email);
        })
        .catch(error => {
          if (error instanceof FirebaseError && error.code === 'auth/expired-action-code') {
            setErrorText(
              'リンクの期限が切れています。パスワード再設定のメール送信から再度お試しください。',
            );
          } else {
            setErrorText('エラーが発生しました。');
          }
          setOpen(true);

          // 3秒後にA2_パスワード忘れへの遷移
          setTimeout(() => {
            navigate('/auth/password_reset', { state: { email: email } });
          }, 3000);
        })
        .finally(() => setLoading(false));
    }
  }, []);

  // パスワード再設定処理
  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      await confirmPasswordReset(auth, code, watch('password'));
      navigate('/auth/password_change/complete');
    } catch (error) {
      let errorText: string;
      if (error instanceof FirebaseError) {
        if (error.code === 'auth/network-request-failed') {
          errorText =
            'ネットワークエラーが発生しました。インターネット接続を確認し、再度お試しください。';
        } else {
          errorText = getErrorMessageFromAuthErrorCode(error.code);
        }
      } else if (error instanceof AxiosError && error.response) {
        errorText = error.response.data.message;
      } else {
        errorText = common;
      }
      setErrorText(errorText);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ログイン状態の場合はAI-OCR画面へ遷移
    if (user) navigate('/ai-ocr');
  }, [user]);

  return (
    <Typography component="div" sx={styles.pageContainer}>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Header />
      <Typography component="div" sx={styles.container}>
        <Typography component="div" sx={styles.PasswordChangeContainer}>
          <Typography sx={styles.title}>パスワード再設定</Typography>
          <Controller
            render={({ field }): JSX.Element => (
              <CustomTextfield
                {...field}
                formMb="24px"
                labelText="メールアドレス"
                placeholder={email}
                requiredText="必須"
                width="400px"
                disabled
              />
            )}
            control={control}
            name="email"
          />

          <Controller
            render={({ field }): JSX.Element => (
              <PasswordTextField
                {...field}
                error={Boolean(errors.password)}
                formMb="24px"
                helperText={errors.password?.message}
                labelText="パスワード"
                requiredText="必須"
                width="400px"
              />
            )}
            control={control}
            name="password"
          />

          <Controller
            render={({ field }): JSX.Element => (
              <PasswordTextField
                {...field}
                error={Boolean(errors.passwordConfirm)}
                formMb="40px"
                helperText={errors.passwordConfirm?.message}
                labelText="パスワード確認用"
                requiredText="必須"
                width="400px"
              />
            )}
            control={control}
            name="passwordConfirm"
          />

          <Button
            disabled={buttonDisabledFlag()}
            onClick={async (): Promise<void> => {
              setFirstSubmit(true);
              await handleSubmit(async () => {
                await onSubmit();
              })();
            }}
            sx={[primaryButtonBackgroundColor, styles.button]}
            variant="contained"
          >
            パスワードを再設定する
          </Button>
        </Typography>
      </Typography>
      <CustomSnackbar
        autoHideDuration={6000}
        open={open}
        onClose={(): void => setOpen(false)}
        text={errorText}
      />
    </Typography>
  );
};

export default PasswordChange;
